// PAYMENT METHODS MIXIN
import {
   BANK_TRANSFER_GATEWAY_ID,
   CARD_GATEWAY_ID,
   INVOICE_GATEWAY_ID,
   PAYPAL_GATEWAY_ID
} from '@/js/funnel/_config/commerce';

/**
 * The library for Stripe's <script>.
 *
 * @type {string}
 */
export const PAYPAL_SCRIPT_ENDPOINT = 'https://www.paypal.com/sdk/js';

/**
 * The library for Stripe's <script>.
 *
 * @type {string}
 */
export const STRIPE_SCRIPT_ENDPOINT = 'https://js.stripe.com/v3/';

/**
 * Styling for Stripe's fields.
 *
 * @return {object}
 */
export const STRIPE_ELEMENTS_STYLES = {
   base: {
      'fontSize': '16px',
      'color': '#001E19',
      'lineHeight': '1.5',
      'fontWeight': '400',
      'fontFamily': 'Poppins, sans-serif',
      '::placeholder': {color: '#b3bbb8'},
      ':-webkit-autofill': {color: '#FAE9E0'}, // accent
   },
   invalid: {
      color: '#001E19',
      iconColor: '#001E19'
   },
};


/**
 * Mixin for handling payment methods in the funnel.
 *
 * @type {{data(): {payPalActionStatus: null, cardElement: {number: null, cvc: null, expiry: null},
 *    payPalErrorShown: boolean, stripeInstance: null}, computed: {cardPaymentData():
 *    {billing_details: {name: string, email: *, address: *}}, cartHasBankTransferGateway():
 *    boolean, cartHasCardGateway(): boolean, cartHasInvoiceGateway(): boolean,
 *    cartHasPayPalGateway(): boolean}, methods: {_initStripe(*=): void, createOrderForPayPal(): *,
 *    goToPaymentMethods(): void, _initPayPal(): void, _handleStripeCardErrors(*): void,
 *    completePaymentFromPayPal(): *, changeGateway(): Promise<void>}}}
 */
export const PaymentMethodsMixin = {
   data() {
      return {
         stripeInstance: null,
         cardElement: {
            number: null,
            expiry: null,
            cvc: null,
         },
         payPalErrorShown: false,
         payPalActionStatus: null,
      }
   },

   computed: {
      /**
       * If the cart's gateway is set to "Card". This gateway will use Stripe's API to process
       * the card transaction.
       *
       * @link http://localhost:8000/mymycms/commerce/settings/gateways
       * @link https://github.com/craftcms/commerce-stripe
       * @returns {boolean}
       */
      cartHasCardGateway() {
         return this.cart.gatewayId === CARD_GATEWAY_ID;
      },

      /**
       * If the cart's gateway is set to "PayPal".
       *
       * @link http://localhost:8000/mymycms/commerce/settings/gateways
       * @link https://github.com/craftcms/commerce-paypal-checkout
       * @returns {boolean}
       */
      cartHasPayPalGateway() {
         return this.cart.gatewayId === PAYPAL_GATEWAY_ID;
      },

      /**
       * If the cart's gateway is set to [bank] "Transfer".
       *
       * @link http://localhost:8000/mymycms/commerce/settings/gateways
       * @returns {boolean}
       */
      cartHasBankTransferGateway() {
         return this.cart.gatewayId === BANK_TRANSFER_GATEWAY_ID;
      },

      /**
       * If the cart's gateway is set to "Invoice".
       *
       * @link http://localhost:8000/mymycms/commerce/settings/gateways
       * @returns {boolean}
       */
      cartHasInvoiceGateway() {
         return this.cart.gatewayId === INVOICE_GATEWAY_ID;
      },

      /**
       * Get the cardholder's meta data for requesting a payment method token from Stripe.
       *
       * @returns {{billing_details: {name: string, email: *, address: *}}}
       */
      cardPaymentData() {
         return {
            billing_details: {
               name: (this.fields.payment.stripe.firstName + ' ' + this.fields.payment.stripe.lastName).trim(),
               email: this.cart.email,
               address: this.fields.payment.stripe.address,
            },
         }
      },
   },

   methods: {
      /**
       * Scroll the customer down to the payment methods section if they click the "pay"
       * button by the order summary.
       */
      goToPaymentMethods() {
         this.modals.mobileSummary.show = false;
         this.$scrollTo('#payment');
      },

      /**
       * Update the cart with a different payment gateway option.
       */
      async changeGateway() {
         this.modals.loading.show = true;

         // store the previous gateway in order to restore it in case of errors
         const previousGatewayId = this.$store.state.cart.gatewayId
         // post to Commerce's controller
         const response = await this.$store.dispatch('updateCart', {
            gatewayId: this.fields.payment.gatewayId,
         });

         if (response?.data?.cart) {
            // this.reloadPage();
            // mutate the already loaded cart without to reload the page
            // ensure to set the proper gatewayId accordingly to server response
            this.fields.payment.gatewayId = response?.data?.cart.gatewayId
            this.$store.commit('setCartPaymentGateway', parseInt(response?.data?.cart.gatewayId));

            this.gtmAddPaymentInfo();
            this.modals.loading.show = false;
         } else if (response?.data?.errors) {
            this.fields.payment.gatewayId = previousGatewayId
            this.$store.commit('setCartPaymentGateway', parseInt(this.fields.payment.gatewayId));
            this.showErrorModal(this.getYiiErrors(response.data.errors));
         } else {
            this.fields.payment.gatewayId = previousGatewayId
            this.$store.commit('setCartPaymentGateway', parseInt(this.fields.payment.gatewayId));
            this.showErrorModal(this.getDefaultErrorMessage(response?.data?.cart?.shortNumber));
            if(window.Sentry) {
               Sentry.captureMessage("Response not handled correctly :159")
            }
         }
      },

      /**
       * Append a <script> tag to the bottom of the <body> tag.
       *
       * @param src {string}
       * @return {Promise<*>}
       */
      async appendScript(src) {
         return new Promise((resolve, reject) => {
            const script = document.createElement('script');

            script.setAttribute('src', src);
            script.addEventListener('load', resolve);
            script.addEventListener('error', reject);

            document.body.appendChild(script);
         });
      },


      // STRIPE GATEWAY
      // =====================================================================================

      /**
       * METHOD: Initialize Stripe's API if the customer's chosen gateway is "Credit Card".
       *
       * Stripe method 1/2.
       *
       * @private
       */
      async _initStripe() {
         // load Stripe's library
         try {
            await this.appendScript(STRIPE_SCRIPT_ENDPOINT);
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
            return;
         }

         // create a Stripe client
         this.stripeInstance = Stripe(window.mymy.paymentMethods.stripe.publishableKey);

         // create an instance of Stripe Elements
         const elements = this.stripeInstance.elements({
            fonts: [{cssSrc: 'https://fonts.googleapis.com/css?family=Poppins'}],
            locale: window.mymy.request.currentSite.language
         });

         // create the field elements that the user will input their data into
         this.cardElement.number = elements.create('cardNumber', {'placeholder': '', 'style': STRIPE_ELEMENTS_STYLES});
         this.cardElement.expiry = elements.create('cardExpiry', {'placeholder': 'MM / YY', 'style': STRIPE_ELEMENTS_STYLES});
         this.cardElement.cvc = elements.create('cardCvc', {'placeholder': '', 'style': STRIPE_ELEMENTS_STYLES});

         // bind field elements to divs we tagged with a Vue ref
         this.cardElement.number.mount(this.$refs.cardNumber);
         this.cardElement.expiry.mount(this.$refs.cardExpiry);
         this.cardElement.cvc.mount(this.$refs.cardCvc);

         // add event listeners to each field element for front-end error handling
         this.cardElement.number.addEventListener('change', this._handleStripeCardErrors);
         this.cardElement.expiry.addEventListener('change', this._handleStripeCardErrors);
         this.cardElement.cvc.addEventListener('change', this._handleStripeCardErrors);
      },

      /**
       * METHOD: Provide front-end validation error handling on Stripe's credit card form.
       *
       * This adds Bootstrap's classes to show the same styling we use on our own form
       * components.
       *
       * @param object event
       * @private
       */
      _handleStripeCardErrors(event) {
         let formControl = this.$refs[event.elementType];
         let invlalidTooltip = this.$refs[event.elementType + 'Error'];
         if (event.error) {
            formControl.classList.add('is-invalid');
            invlalidTooltip.textContent = event.error.message;
         }
         else {
            formControl.classList.remove('is-invalid');
            invlalidTooltip.textContent = '';
         }
      },


      // PAYPAL GATEWAY
      // ===========================================================================================

      /**
       * Initialize PayPal's buttons if the customer's chosen gateway is PayPal.
       *
       * PayPal method 1/3.
       *
       * @private
       */
      async _initPayPal() {
         // load PayPal's library
         try {
            await this.appendScript(PAYPAL_SCRIPT_ENDPOINT + '?' + [
               'client-id=' + window.mymy.paymentMethods.payPal.clientApiKey,
               'currency=' + window.mymy.request.currency,
               'disable-funding=card',
            ].join('&'));
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
            return;
         }

         paypal.Buttons({
            createOrder: (data, actions) => {
               return this.createOrderForPayPal();
            },
            onError: (error) => {
               this.showErrorModal(this.translations.responses.genericPayPalError);
            },
            onCancel: () => {
               this.modals.loading.show = false;
            },
            onApprove: (data, actions) => {
               return this.completePaymentFromPayPal();
            }
         }).render('#paypal-button-container');
      },

      /**
       * Submit the order to Commerce, which will talk to PayPal for us and return a
       * transaction ID that PayPal needs to complete the transaction.
       *
       * PayPal method 2/3.
       *
       * @returns {*}
       */
      async createOrderForPayPal() {
         this.modals.loading.show = true;
         this.dataToSubmit['gatewayId'] = this.fields.payment.gatewayId;
         this.dataToSubmit['orderEmail'] = this.cart.email;
         this._finalizeDataToSubmit();

         const response = await this.$store.dispatch('makePayment', this.dataToSubmit);

         if (response?.data?.order) {
            this.fields.payment.paypal.transactionHash = response.data.transactionHash;
            return response.data.transactionId;
         }
         else if (response?.data?.errors) {
            this.showErrorModal(this.getYiiErrors(response.data.errors));
         }
         else {
            this.showErrorModal(this.getDefaultErrorMessage(response?.data?.cart?.shortNumber));
            if(window.Sentry) {
               Sentry.captureMessage("Response not handled correctly :319")
            }
         }
      },

      /**
       * Complete the payment after the user has paid the total in the PayPal popup modal.
       *
       * PayPal method 3/3.
       *
       * @returns {*}
       */
      async completePaymentFromPayPal() {
         this.dataToSubmit['commerceTransactionHash'] = this.fields.payment.paypal.transactionHash;

         // post to Commerce's controller
         const response = await this.makePostRequest('commerce/payments/complete-payment', this.dataToSubmit);

         this.redirectUser(response.data.url);
      },
   },
};